import axios from "axios";

const BACKEND_URL =
  (process.env.NODE_ENV === "development" && "http://127.0.0.1:8000") || "";

class Auth0Service {
  constructor() {
    this.api = axios.create({
      headers: {
        "Content-Type": "application/json",
      },
    });
  }

  async login(email, password) {
    try {
      // Basic client-side validation
      if (!password) {
        throw new Error("Password is required");
      }

      if (password.length < 8) {
        throw new Error("Password must be at least 8 characters");
      }

      const response = await this.api.post(`${BACKEND_URL}/api/auth/login`, {
        email,
        password,
      });

      console.log("Login response:", response.data);

      // Check for valid login
      if (response.data?.user && response.data?.access_token) {
        const user = response.data.user;

        // Use `user_id` instead of `id`
        if (!user.user_id || !user.email) {
          throw new Error("Invalid user data received from server");
        }

        this.setAuthToken(response.data.access_token);
        return response.data;
      }

      throw new Error("Login failed. Invalid server response.");
    } catch (error) {
      console.error("Login Error:", error.response?.data || error.message);

      const rawMessage = error.response?.data?.message || error.message;

      if (
        rawMessage.toLowerCase().includes("user not found") ||
        rawMessage.toLowerCase().includes("invalid email")
      ) {
        throw new Error("Invalid email address");
      } else if (rawMessage.toLowerCase().includes("invalid password")) {
        throw new Error("Invalid password");
      } else if (rawMessage.toLowerCase().includes("invalid credentials")) {
        throw new Error("Invalid email or password");
      }

      throw new Error(
        "Login failed. Please check your credentials and try again."
      );
    }
  }

  async register(name, email, password) {
    try {
      // const response = await this.api.post(`${BACKEND_URL}/api/register`, {
      //   name,
      //   email,
      //   password
      // });
      const response = await this.api.post(`${BACKEND_URL}/api/auth/register`, {
        name,
        email,
        password,
      });

      if (response.data.user) {
        return {
          success: true,
          message:
            "Registration successful! Please log in with your credentials.",
        };
      }
    } catch (error) {
      console.error("Registration Error Details:", error.response?.data);

      if (error.response?.data?.message) {
        const message = error.response.data.message;
        if (message.includes("PasswordStrengthError")) {
          throw new Error(
            "Password is too weak. Please use a stronger password with at least 8 characters, including uppercase, lowercase, numbers, and special characters."
          );
        } else if (message.includes("invalid_email")) {
          throw new Error("Please enter a valid email address.");
        } else if (message.includes("invalid_password")) {
          throw new Error(
            "Password does not meet requirements. Please use a stronger password."
          );
        } else if (message.includes("user_exists")) {
          throw new Error("User already exists. Please log in instead.");
        } else {
          throw new Error(message);
        }
      }

      throw new Error("Registration failed. Please try again.");
    }
  }

  async loginWithGoogle(credential) {
    try {
      const response = await this.api.post(`${BACKEND_URL}/api/auth/google`, {
        credential,
      });

      if (response.data?.user && response.data?.access_token) {
        this.setAuthToken(response.data.access_token);
        return response.data;
      }

      throw new Error("Google login failed. Invalid server response.");
    } catch (error) {
      console.error("Google Login Error:", error.response?.data || error.message);
      throw new Error(error.response?.data?.message || "Google login failed.");
    }
  }

  setAuthToken(token) {
    if (token) {
      this.api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      localStorage.setItem("authToken", token);
    } else {
      delete this.api.defaults.headers.common["Authorization"];
      localStorage.removeItem("authToken");
    }
  }

  getAuthToken() {
    return localStorage.getItem("authToken");
  }

  logout() {
    this.setAuthToken(null);
  }

  async sendVerificationCode(email) {
    try {
      const response = await this.api.post(`${BACKEND_URL}/api/auth/send-verification`, { email });
      return response.data;
    } catch (error) {
      console.error("Send Verification Error:", error.response?.data || error.message);
      throw new Error(error.response?.data?.error || "Failed to send verification code.");
    }
  }

  async verifyCode(email, code, verificationToken) {
    try {
      const response = await this.api.post(`${BACKEND_URL}/api/auth/verify-code`, { 
        email, 
        code,
        verification_token: verificationToken 
      });
      return response.data;
    } catch (error) {
      console.error("Verify Code Error:", error.response?.data || error.message);
      throw new Error(error.response?.data?.error || "Invalid verification code.");
    }
  }
}

const auth0Service = new Auth0Service();
export default auth0Service;
